import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useDrawer, type DrawerKind, type DrawerStackEntry } from '@/app/providers';
import { Button } from '@/design-system/Button';
import { Kicker } from '@/design-system/Kicker';
import {
  EntityFormDrawer,
  type EntityFormDrawerProps,
} from '@/design-system/EntityFormDrawer';
import { getCitationSource } from '@/versions/v1/shared/citation-registry';
import MeetingCitationDrawerBody from '@/versions/v1/modules/changelog/MeetingCitationDrawerBody';
import { CaseDrawer } from '@/versions/v1/chrome/drawers/CaseDrawer';
import { AssetWorkOrdersDrawer } from '@/versions/v1/modules/maintenance/AssetWorkOrdersDrawer';

// Drawer — universal right-anchored drawer chrome. Scenes push entries via
// `useDrawer().open(kind, props)`; this component renders the topmost entry.
// To add a module-owned drawer: add the kind to DrawerKind in
// src/app/providers.tsx, import the body component here, and extend the
// renderBody switch (plus KINDS_WITH_OWN_FOOTER when the body carries its
// own CTA row).

// Kinds that own their footer (CTA row rendered inside the body component).
// The chrome suppresses its default Back/Confirm footer for these.
const KINDS_WITH_OWN_FOOTER: ReadonlySet<DrawerKind> = new Set([
  'citation',
  'entity-form',
  'meeting-citation',
  'case-drawer',
  'work-order-list',
]);

// Map kind → the registered body component. `placeholder` falls through to
// the inline stub below so deferred drawers still read clearly.
function renderBody(entry: DrawerStackEntry) {
  const { kind, props } = entry;
  const data = (props.data ?? {}) as Record<string, unknown>;

  switch (kind) {
    case 'entity-form':
      return (
        <EntityFormDrawer
          {...(data as unknown as EntityFormDrawerProps)}
        />
      );
    case 'citation':
      return <CitationBody data={data as { citationId?: string }} />;
    case 'meeting-citation':
      return (
        <MeetingCitationDrawerBody
          data={data as { meetingId?: string; entryId?: string }}
        />
      );
    case 'case-drawer':
      return <CaseDrawer props={props} />;
    case 'work-order-list':
      return <AssetWorkOrdersDrawer data={data as { assetId?: string }} />;
    case 'placeholder':
    default:
      return <PlaceholderBody entry={entry} />;
  }
}

// Citation body — resolves a cite id against the citation registry and shows
// the source excerpt. The AIRail's answer chips open this.
function CitationBody({ data }: { data: { citationId?: string } }) {
  const source = data.citationId ? getCitationSource(data.citationId) : undefined;
  if (!source) {
    return (
      <div className="flex-1 p-5 overflow-y-auto scroll-soft">
        <p style={{ color: 'var(--ink-secondary)', fontSize: 13 }}>
          No source registered for this citation.
        </p>
      </div>
    );
  }
  return (
    <div className="flex-1 p-5 overflow-y-auto scroll-soft">
      <blockquote
        style={{
          borderLeft: '2px solid var(--accent)',
          paddingLeft: 12,
          color: 'var(--ink-primary)',
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        {source.excerpt}
      </blockquote>
      <dl
        className="mt-5"
        style={{ color: 'var(--ink-secondary)', fontSize: 12, lineHeight: 1.8 }}
      >
        <div>
          <span style={{ color: 'var(--ink-tertiary)' }}>Source · </span>
          {source.documentFilename}
        </div>
        <div>
          <span style={{ color: 'var(--ink-tertiary)' }}>Author · </span>
          {source.author}
        </div>
        <div>
          <span style={{ color: 'var(--ink-tertiary)' }}>Date · </span>
          <span className="tabular">{source.date}</span>
        </div>
        <div>
          <span style={{ color: 'var(--ink-tertiary)' }}>Confidence · </span>
          {source.confidence}
        </div>
      </dl>
    </div>
  );
}

// Inline stub for unregistered / placeholder drawers — keeps "Detail view —
// under construction" coherent for any kind not wired yet.
function PlaceholderBody({ entry }: { entry: DrawerStackEntry }) {
  const { kind, props } = entry;
  return (
    <div className="flex-1 p-5 overflow-y-auto scroll-soft">
      <div
        style={{
          border: '1px dashed var(--hairline-strong)',
          background: 'var(--surface-1)',
          borderRadius: '8px',
          padding: '8px 12px',
          display: 'inline-block',
          color: 'var(--ink-secondary)',
          fontSize: '11px',
          letterSpacing: '0.04em',
        }}
      >
        <span
          style={{
            color: 'var(--accent)',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginRight: '8px',
          }}
        >
          Stub
        </span>
        Drawer kind: {kind} — under construction
      </div>
      {props.data ? (
        <div className="mt-4 text-xs" style={{ color: 'var(--ink-tertiary)' }}>
          {Object.entries(props.data).map(([k, v]) => (
            <div key={k}>
              <span style={{ color: 'var(--ink-secondary)' }}>{k}:</span> {String(v)}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Default kicker / title labels — used when the caller didn't supply them.
function defaultKicker(kind: DrawerKind): string {
  switch (kind) {
    case 'entity-form':
      return 'Create';
    case 'citation':
      return 'Citation';
    case 'meeting-citation':
      return 'Meeting citation';
    case 'case-drawer':
      return 'Case';
    case 'work-order-list':
      return 'Asset work orders';
    case 'placeholder':
    default:
      return 'Drawer';
  }
}

function defaultTitle(kind: DrawerKind): string {
  switch (kind) {
    case 'entity-form':
      return 'New entity';
    case 'citation':
      return 'Source excerpt';
    case 'meeting-citation':
      return 'Meeting citation';
    case 'case-drawer':
      return 'Case detail';
    case 'work-order-list':
      return 'Open work orders';
    case 'placeholder':
    default:
      return 'Drawer placeholder';
  }
}

export function Drawer() {
  const { stack, close, closeAll } = useDrawer();
  const top = stack.length > 0 ? stack[stack.length - 1] : null;

  // Lock body scroll while any drawer is open; restore on close.
  useEffect(() => {
    if (top) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [top]);

  if (!top) return null;

  const ownsFooter = KINDS_WITH_OWN_FOOTER.has(top.kind);

  return (
    <>
      <div
        className="drawer-backdrop"
        onClick={() => closeAll()}
        aria-hidden="true"
      />
      <aside
        data-comment-anchor="Drawer"
        className={['drawer', 'open', top.props.wide ? 'wide' : ''].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={top.props.title ?? defaultTitle(top.kind)}
      >
        <header className="px-5 py-4 hairline-b flex items-center justify-between flex-shrink-0">
          <div>
            <Kicker>{top.props.kicker ?? defaultKicker(top.kind)}</Kicker>
            <h2
              className="mt-1"
              style={{
                color: 'var(--ink-primary)',
                fontSize: '15px',
                fontWeight: 500,
              }}
            >
              {top.props.title ?? defaultTitle(top.kind)}
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close drawer"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--ink-tertiary)',
              padding: '4px',
            }}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        {renderBody(top)}
        {!ownsFooter && (
          <footer className="px-5 py-4 hairline-t flex items-center gap-3 flex-shrink-0">
            <Button variant="ghost" onClick={close}>
              Back
            </Button>
            <Button variant="primary" className="ml-auto" onClick={close}>
              Confirm
            </Button>
          </footer>
        )}
      </aside>
    </>
  );
}
